// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { maxLengthAttr } from "@bg/XDL/Utilities/inputValidation";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import applyPriceBreakDiscount from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/ApplyPriceBreakDiscount";
import removePriceBreak from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/RemovePriceBreak";
import updatePriceBreakMaxQty from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/UpdatePriceBreakMaxQty";
import updatePriceBreakPrice from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/UpdatePriceBreakPrice";
import updatePriceBreakStartQty from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/UpdatePriceBreakStartQty";
import translate from "@insite/client-framework/Translate";
import { BreakPriceRfqModel, QuoteLineModel, QuoteModel } from "@insite/client-framework/Types/ApiModels";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Select, { SelectProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    quote: QuoteModel;
    quoteLine: QuoteLineModel;
    priceBreak: BreakPriceRfqModel;
    index: number;
    calculatorIsOpen: boolean;
    openCalculator: (index: number) => void;
    isFixedQty: boolean;
    minQty: string;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    validation: state.pages.rfqQuoteDetails.priceBreakValidations.find(o => o.index === ownProps.index),
    priceBreaks: state.pages.rfqQuoteDetails.quoteLineForCalculation?.pricingRfq?.priceBreaks,
});

const mapDispatchToProps = {
    updatePriceBreakStartQty,
    updatePriceBreakMaxQty,
    updatePriceBreakPrice,
    applyPriceBreakDiscount,
    removePriceBreak,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RfqQuoteDetailsQuoteLineCalculatorStyles {
    priceBreakRow?: InjectableCss;
    priceBreakWithCalculatorRow?: InjectableCss;
    qtyCell?: InjectableCss;
    qtyOrderedTextField?: TextFieldPresentationProps;
    startQtyTextField?: TextFieldPresentationProps;
    toLabelText?: TypographyPresentationProps;
    endQtyTextField?: TextFieldPresentationProps;
    priceCell?: InjectableCss;
    priceTextField?: TextFieldPresentationProps;
    buttonsCell?: InjectableCss;
    removePriceBreakLink?: LinkPresentationProps;
    removePriceBreakIcon?: IconPresentationProps;
    calculatorCell?: InjectableCss;
    calculatorContainer?: InjectableCss;
    methodGridItem?: InjectableCss;
    methodSelect?: SelectProps;
    percentGridItem?: InjectableCss;
    percentTextField?: TextFieldPresentationProps;
    percentIcon?: IconPresentationProps;
    buttonsGridItem?: InjectableCss;
    applyDiscountLink?: LinkPresentationProps;
    applyDiscountIcon?: IconPresentationProps;
}

export const rfqQuoteDetailsQuoteLineCalculatorStyles: RfqQuoteDetailsQuoteLineCalculatorStyles = {
    priceBreakRow: {
        css: css`
            display: flex;
            align-items: center;
            margin-bottom: 15px;
            flex-wrap: wrap;
        `,
    },
    qtyCell: {
        css: css`
            flex-grow: 1;
            display: flex;
            align-items: center;
        `,
    },
    toLabelText: {
        css: css`
            margin-right: 5px;
        `,
    },
    startQtyTextField: {
        cssOverrides: {
            formField: css`
                max-width: 60px;
                margin-right: 5px;
                input {
                    text-align: center;
                }
            `,
            formInputWrapper: css`
                margin-top: 0 !important;
            `,
        },
    },
    endQtyTextField: {
        cssOverrides: {
            formField: css`
                max-width: 60px;
                margin-left: 5px;
                input {
                    text-align: center;
                }
            `,
            formInputWrapper: css`
                margin-top: 0 !important;
            `,
        },
    },
    priceCell: {
        css: css`
            max-width: 145px;
            display: flex;
            align-items: center;
        `,
    },
    priceTextField: {
        cssOverrides: {
            formField: css`
                flex-direction: row;
                align-items: center;
                background: ${getColor("common.ui4")};
                max-width: 95px;
                margin-right: 30px;
                align-items: center;
                border-radius: 4px;
                padding-left: 5px;
                label {
                    width: auto;
                    font-size: 16px;
                    font-weight: 500;
                }
            `,
            formInputWrapper: css`
                margin-top: 0 !important;
            `,
        },
    },
    calculatorContainer: {
        css: css`
            width: 100%;
            display: flex;
            align-items: center;
            margin-top: 10px;
        `,
    },
    methodGridItem: {
        css: css`
            flex-grow: 1;
            margin-right: 40px;
        `,
    },
    percentIcon: {
        src: "Percent",
    },
    percentGridItem: {
        css: css`
            margin-right: 30px;
            display: flex;
            align-items: center;
        `,
    },
    percentTextField: {
        cssOverrides: {
            formField: css`
                max-width: 72px;
            `,
        },
    },
    removePriceBreakIcon: {
        size: 18,
        color: "secondary",
        src: "Delete",
    },
};

export const styles = rfqQuoteDetailsQuoteLineCalculatorStyles;

const RfqQuoteDetailsQuoteLineCalculatorRow = ({
    quote,
    quoteLine,
    priceBreak,
    index,
    calculatorIsOpen,
    openCalculator,
    validation,
    updatePriceBreakStartQty,
    updatePriceBreakMaxQty,
    updatePriceBreakPrice,
    applyPriceBreakDiscount,
    removePriceBreak,
    isFixedQty,
    minQty,
    priceBreaks,
}: Props) => {
    const [startQty, setStartQty] = useState(priceBreak.startQty);
    const [endQty, setEndQty] = useState(!priceBreak.endQty ? "Max" : `${priceBreak.endQty}`);
    const [price, setPrice] = useState(priceBreak.price || "");
    const [calculationMethod, setCalculationMethod] = useState(quoteLine?.pricingRfq?.calculationMethods?.[0]);
    const [percent, setPercent] = useState(0);
    const dispatch = useAppDispatch();

    useEffect(() => {
        setStartQty(priceBreak.startQty);
        setEndQty(!priceBreak.endQty ? "Max" : `${priceBreak.endQty}`);
        setPrice(priceBreak.price);
    }, [priceBreak]);

    useEffect(() => {
        if (parseInt(minQty, 10) > startQty || isFixedQty) {
            setStartQty(parseInt(minQty, 10));
            updatePriceBreakStartQty({ index, startQty: parseInt(minQty, 10) || 0, isFixedQty, minQty } as any);
        }
    }, [minQty]);
    
    useEffect(() => {
        if (isFixedQty && priceBreaks && priceBreaks.length > 0) {
            const priceBreak = {
                ...priceBreaks[0],
                endQty: 0,
                endQtyDisplay: null,
            };
            const updatedPriceBreaks = [priceBreak];
            dispatch({
                type: "Pages/RfqQuoteDetails/UpdatePriceBreaks",
                updatedPriceBreaks,
            });
        }
    }, [isFixedQty]);

    const startQtyChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(event.currentTarget.value, 10) || 0;
        // eslint-disable-next-line no-restricted-globals
        setStartQty(isNaN(value) ? startQty : value);
    };

    const startQtyBlurHandler = () => {
        const minQtyValue = parseInt(minQty, 10);
        // eslint-disable-next-line no-restricted-globals
        if ((minQty !== "" && minQtyValue && startQty < minQtyValue) || isNaN(startQty)) {
            setStartQty(minQtyValue);
            updatePriceBreakStartQty({ index, startQty: minQtyValue || 0, isFixedQty, minQty } as any);
        } else {
            updatePriceBreakStartQty({ index, startQty: startQty || 0, isFixedQty, minQty } as any);
        }
    };

    const endQtyChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setEndQty(event.currentTarget.value);
    };

    const endQtyBlurHandler = () => {
        updatePriceBreakMaxQty({ index, maxQty: endQty, isFixedQty, minQty } as any);
    };

    const priceChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const price = parseFloat(event.currentTarget.value);
        setPrice(event.target.value);
    };

    const priceFocusHandler = () => {
        openCalculator(index);
    };

    const priceBlurHandler = () => {
        if ((price as any) === "") {
            setPrice(priceBreak.price);
            return;
        }
        updatePriceBreakPrice({ index, price: Number(price) || 0, isFixedQty, minQty } as any);
    };

    const calculationMethodChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
        setCalculationMethod(
            quoteLine?.pricingRfq?.calculationMethods?.find(o => o.name === event.currentTarget.value),
        );
    };

    const percentChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setPercent(parseFloat(event.currentTarget.value));
    };

    const percentBlurHandler = () => {
        setPercent(percent || 0);
    };

    const applyDiscountClickHandler = () => {
        if (!calculationMethod) {
            return;
        }

        applyPriceBreakDiscount({
            index,
            calculationMethod: calculationMethod.value,
            percent,
            isFixedQty,
            minQty,
        } as any);
    };

    const removePriceBreakClickHandler = () => {
        removePriceBreak({ index, isFixedQty, minQty } as any);
    };

    return (
        <StyledWrapper {...styles.priceBreakRow} data-test-selector="quoteModal_priceBreaksRow">
            <StyledWrapper {...styles.qtyCell}>
                {quote.isJobQuote ? (
                    <>
                        <VisuallyHidden>{translate("Quantity Ordered")}</VisuallyHidden>
                        <TextField {...styles.qtyOrderedTextField} value={quoteLine.qtyOrdered || 1} disabled />
                    </>
                ) : (
                    <>
                        <VisuallyHidden>{translate("From")}</VisuallyHidden>
                        <TextField
                            {...styles.startQtyTextField}
                            value={isFixedQty ? minQty || 1 : startQty}
                            type="text"
                            disabled={isFixedQty}
                            maxLength={maxLengthAttr.qty}
                            error={validation?.invalidQty && !isFixedQty && !minQty}
                            onChange={startQtyChangeHandler}
                            onBlur={startQtyBlurHandler}
                            data-test-selector={`quoteModal_startQty_${index}`}
                        />
                        {!isFixedQty && <Typography {...styles.toLabelText}>{translate("to")}</Typography>}
                        {!isFixedQty && (
                            <TextField
                                {...styles.endQtyTextField}
                                disabled={index !== quoteLine.pricingRfq!.priceBreaks!.length - 1}
                                value={endQty}
                                maxLength={maxLengthAttr.qty}
                                error={validation?.invalidQty && !minQty}
                                onChange={endQtyChangeHandler}
                                onBlur={endQtyBlurHandler}
                            />
                        )}
                    </>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.priceCell}>
                <VisuallyHidden>{translate("Price")}</VisuallyHidden>
                <TextField
                    {...styles.priceTextField}
                    value={price}
                    type="number"
                    label="$"
                    min={0}
                    error={validation?.priceRequired || validation?.invalidPrice}
                    onChange={priceChangeHandler}
                    onFocus={priceFocusHandler}
                    onBlur={priceBlurHandler}
                    data-test-selector={`quoteModal_priceBreaks_${index}`}
                />
                <StyledWrapper {...styles.buttonsCell}>
                    {quoteLine.pricingRfq?.priceBreaks && !quote.isJobQuote && (
                        <Link
                            disabled={isFixedQty}
                            {...styles.removePriceBreakLink}
                            onClick={removePriceBreakClickHandler}
                        >
                            <VisuallyHidden>{translate("Remove price break")}</VisuallyHidden>
                            <Icon {...styles.removePriceBreakIcon} />
                        </Link>
                    )}
                </StyledWrapper>
            </StyledWrapper>
            {calculatorIsOpen && calculationMethod && (
                <StyledWrapper {...styles.calculatorContainer}>
                    <StyledWrapper {...styles.methodGridItem}>
                        <Select
                            {...styles.methodSelect}
                            value={calculationMethod?.name}
                            onChange={calculationMethodChangeHandler}
                            data-test-selector="quoteLineCalculator_QuoteModal_DiscountBy"
                        >
                            {quoteLine.pricingRfq?.calculationMethods?.map(cm => (
                                <option key={cm.name} value={cm.name}>
                                    {cm.displayName}
                                </option>
                            ))}
                        </Select>
                    </StyledWrapper>
                    <StyledWrapper {...styles.percentGridItem}>
                        <VisuallyHidden>{translate("Percent")}</VisuallyHidden>
                        <TextField
                            {...styles.percentTextField}
                            type="number"
                            min={calculationMethod?.minimumMargin}
                            max={
                                calculationMethod && parseFloat(calculationMethod.maximumDiscount) > 0
                                    ? parseFloat(calculationMethod.maximumDiscount)
                                    : undefined
                            }
                            value={percent}
                            onChange={percentChangeHandler}
                            onBlur={percentBlurHandler}
                            data-test-selector="quoteLineCalculator_QuoteModal_CalculationPercent"
                        />
                        <Icon {...styles.percentIcon} />
                    </StyledWrapper>
                    <StyledWrapper {...styles.buttonsGridItem}>
                        <Link
                            {...styles.applyDiscountLink}
                            onClick={applyDiscountClickHandler}
                            data-test-selector="quoteLineCalculator_apply"
                        >
                            <VisuallyHidden>{translate("Apply discount")}</VisuallyHidden>
                            <Icon src="Check" size={18} color="primary" />
                        </Link>
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(RfqQuoteDetailsQuoteLineCalculatorRow);
